/* Modal de perfil da IA — especialidade, idioma, estilo, tópicos, constraints */
import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import type { AIProfile } from '../types';
import { getStoredJwt } from '../api/client';
import {
  fetchProfile,
  pushWebProfileToServer,
  serverProfileTemDados,
  serverProfileToWeb,
  webProfileTemDados,
} from '../api/profile';

/** Chave de persistência no localStorage */
const STORAGE_KEY = 'drpera-ia-profile';

/** Lista de idiomas suportados pelo modelo de IA (não os da UI) */
const IDIOMAS_IA = [
  { code: 'pt-BR', label: 'Português Brasil' },
  { code: 'pt-PT', label: 'Português Portugal' },
  { code: 'es', label: 'Español LatAm' },
  { code: 'es-ES', label: 'Español España' },
  { code: 'en-US', label: 'English US' },
  { code: 'en-GB', label: 'English UK' },
  { code: 'it', label: 'Italiano' },
  { code: 'fr', label: 'Français' },
  { code: 'fr-CA', label: 'Français Canada' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'pl', label: 'Polski' },
  { code: 'ru', label: 'Русский' },
  { code: 'uk', label: 'Українська' },
  { code: 'cs', label: 'Čeština' },
  { code: 'hu', label: 'Magyar' },
  { code: 'ro', label: 'Română' },
  { code: 'sv', label: 'Svenska' },
  { code: 'el', label: 'Ελληνικά' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'العربية' },
  { code: 'he', label: 'עברית' },
  { code: 'zh-CN', label: '中文简体' },
  { code: 'zh-TW', label: '中文繁體' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'th', label: 'ไทย' },
  { code: 'id', label: 'Bahasa Indonesia' },
  { code: 'tl', label: 'Filipino' },
  { code: 'sw', label: 'Kiswahili' },
];

/** Perfil padrão vazio */
const PERFIL_PADRAO: AIProfile = {
  specialty: '',
  idioma: 'pt-BR',
  communicationStyle: '',
  topicsOfInterest: '',
  constraints: '',
};

/** Carrega perfil do localStorage */
function carregarPerfil(): AIProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return PERFIL_PADRAO;
    return { ...PERFIL_PADRAO, ...(JSON.parse(raw) as Partial<AIProfile>) };
  } catch {
    return PERFIL_PADRAO;
  }
}

/** Persiste perfil no localStorage */
function salvarPerfil(perfil: AIProfile): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(perfil));
}

/**
 * Apaga o perfil local (logout completo, 21/08/2026 — PC compartilhada de
 * UPA/hospital: "sair" não pode deixar rastro do médico). O perfil real
 * vive no backend (AI_PROFILES) e volta no próximo login.
 */
export function clearStoredProfile(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage indisponível — nada a apagar */
  }
}

interface ProfileModalProps {
  onClose: () => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/**
 * Modal de perfil da IA.
 * Persiste no localStorage (cache/fallback) e sincroniza com o backend
 * GET/PUT /api/ai/profile quando o médico está logado (best-effort).
 * Dropdown de 33 idiomas para a IA responder no idioma preferido do médico.
 */
export const ProfileModal = forwardRef<HTMLDivElement, ProfileModalProps>(
  ({ onClose, t }, ref) => {
    const [perfil, setPerfil] = useState<AIProfile>(carregarPerfil);
    // true depois que o médico editou algum campo — evita que o GET tardio
    // do backend sobrescreva digitação em andamento
    const editouRef = useRef(false);

    // Sync com backend ao abrir (best-effort, só quando logado):
    // - server COM dados → prioridade sobre localStorage (atualiza cache local)
    // - server VAZIO + localStorage com dados → push (migração suave única)
    // Falha (offline, 401) não quebra nada — localStorage segue como fallback.
    useEffect(() => {
      if (!getStoredJwt()) return;
      let cancelado = false;
      (async () => {
        try {
          const server = await fetchProfile();
          if (cancelado || server === null) return;
          if (serverProfileTemDados(server)) {
            const web = serverProfileToWeb(server);
            salvarPerfil(web); // atualiza cache local
            if (!editouRef.current) setPerfil(web);
          } else {
            const local = carregarPerfil();
            if (webProfileTemDados(local)) {
              await pushWebProfileToServer(local);
            }
          }
        } catch (err) {
          console.warn('[perfil] GET /api/ai/profile falhou — usando localStorage', err);
        }
      })();
      return () => {
        cancelado = true;
      };
    }, []);

    // Fecha ao pressionar Escape
    useEffect(() => {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
      }
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const handleSalvar = useCallback(() => {
      salvarPerfil(perfil);
      // Best-effort: PUT no backend preservando learnings (fire-and-forget)
      void pushWebProfileToServer(perfil);
      onClose();
    }, [perfil, onClose]);

    const handleCampo = useCallback(
      (campo: keyof AIProfile) => (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
        editouRef.current = true;
        setPerfil((prev) => ({ ...prev, [campo]: e.target.value }));
      },
      [],
    );

    /* Estilos reutilizáveis */
    const labelStyle: React.CSSProperties = {
      fontFamily: 'var(--font-sans)',
      fontSize: '12px',
      color: 'var(--ink-mute)',
      marginBottom: '6px',
      display: 'block',
      letterSpacing: '0.04em',
    };

    const textareaStyle: React.CSSProperties = {
      width: '100%',
      padding: '8px 12px',
      background: 'var(--ink)',
      border: '1px solid var(--ink-mute)',
      color: 'var(--ink-light)',
      fontFamily: 'var(--font-sans)',
      fontSize: '13px',
      lineHeight: 1.6,
      resize: 'vertical',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 180ms ease-out',
    };

    return (
      /* Backdrop */
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('profile.title')}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(34, 37, 50, 0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 600,
          padding: '20px',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Card central */}
        <div
          ref={ref}
          style={{
            maxWidth: '540px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'var(--ink-soft)',
            border: '1px solid var(--violet)',
            boxShadow: '0 0 24px rgba(124,124,248,0.18)',
            padding: '32px',
          }}
        >
          {/* Título */}
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '15px',
              fontWeight: 700,
              color: '#FFFFFF',
              textShadow: 'none',
              letterSpacing: '0.04em',
              marginBottom: '6px',
            }}
          >
            {t('profile.title')}
          </div>

          {/* Subtítulo sutil */}
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: '13px',
              color: 'var(--ink-mute)',
              marginBottom: '28px',
              letterSpacing: '0.03em',
            }}
          >
            {t('profile.subtitle')}
          </div>

          {/* Campo: Especialidade */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>{t('profile.specialty.label')}</label>
            <textarea
              value={perfil.specialty}
              onChange={handleCampo('specialty')}
              placeholder={t('profile.specialty.placeholder')}
              rows={2}
              style={textareaStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            />
          </div>

          {/* Campo: Idioma da IA (dropdown) */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                ...labelStyle,
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
              }}
            >
              {t('profile.idioma.label')}
            </label>
            <select
              value={perfil.idioma}
              onChange={handleCampo('idioma')}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: 'var(--ink)',
                border: '1px solid var(--ink-mute)',
                color: 'var(--ink-light)',
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                outline: 'none',
                cursor: 'pointer',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            >
              {IDIOMAS_IA.map(({ code, label }) => (
                <option key={code} value={code}>
                  {code} — {label}
                </option>
              ))}
            </select>
          </div>

          {/* Campo: Estilo de comunicação */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>{t('profile.communicationStyle.label')}</label>
            <textarea
              value={perfil.communicationStyle}
              onChange={handleCampo('communicationStyle')}
              placeholder={t('profile.communicationStyle.placeholder')}
              rows={2}
              style={textareaStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            />
          </div>

          {/* Campo: Tópicos de interesse */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>{t('profile.topicsOfInterest.label')}</label>
            <textarea
              value={perfil.topicsOfInterest}
              onChange={handleCampo('topicsOfInterest')}
              placeholder={t('profile.topicsOfInterest.placeholder')}
              rows={2}
              style={textareaStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            />
          </div>

          {/* Campo: Constraints / regras */}
          <div style={{ marginBottom: '28px' }}>
            <label style={labelStyle}>{t('profile.constraints.label')}</label>
            <textarea
              value={perfil.constraints}
              onChange={handleCampo('constraints')}
              placeholder={t('profile.constraints.placeholder')}
              rows={2}
              style={textareaStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            />
          </div>

          {/* Botões */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                fontWeight: 600,
                padding: '10px 20px',
                background: 'transparent',
                border: '1px solid rgba(124,124,248,0.40)',
                color: 'var(--ink-light)',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                transition: 'box-shadow 180ms ease, border-color 180ms ease, color 180ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FF6A00';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.boxShadow = '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(124,124,248,0.40)';
                e.currentTarget.style.color = 'var(--ink-light)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('profile.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSalvar}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '12px',
                fontWeight: 600,
                padding: '10px 20px',
                background: 'var(--cta-blue)',
                border: '1px solid var(--cta-blue)',
                color: '#FFFFFF',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                boxShadow: 'none',
                transition: 'box-shadow 180ms ease, border-color 180ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 14px rgba(0,96,240,0.7), 0 0 28px rgba(0,96,240,0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('profile.save')}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

ProfileModal.displayName = 'ProfileModal';
